import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { chatService } from '../../services/chatService';
import axios from 'axios';

interface FileAttachment {
  id: number;
  filename: string;
  original_filename: string;
  url: string;
  size: number;
  mime_type: string;
}

interface Message {
  id: number;
  content: string;
  sender_name: string;
  sender_role: string;
  sent_at: string;
  attachments?: FileAttachment[];
}

interface ChatSession {
  id: number;
  subject: string;
  status: string;
}

export const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    initializeChat();
  }, []);

  const initializeChat = async () => {
    try {
      const sessions = await chatService.getSessions();
      
      if (sessions.length > 0) {
        const session = sessions[0];
        setCurrentSession(session);
        await loadMessages(session.id);
      } else {
        await createNewSession();
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
    }
  };

  const createNewSession = async () => {
    try {
      const session = await chatService.createSession({
        subject: 'Chat Support',
        priority: 'normal',
      });
      setCurrentSession(session);
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const loadMessages = async (sessionId: number) => {
    try {
      const messages = await chatService.getMessages(sessionId);
      setMessages(messages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (messageId?: number): Promise<FileAttachment[]> => {
    if (selectedFiles.length === 0) return [];

    setUploadingFiles(true);
    const formData = new FormData();
    selectedFiles.forEach(file => formData.append('files', file));
    if (messageId) formData.append('message_id', messageId.toString());

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/files/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data.files;
    } catch (error) {
      console.error('Failed to upload files:', error);
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSendMessage = async () => {
    if ((message.trim() || selectedFiles.length > 0) && currentSession) {
      setLoading(true);
      try {
        const tempMessage: Message = {
          id: Date.now(),
          content: message || (selectedFiles.length > 0 ? '[File attachment]' : ''),
          sender_name: user?.name || 'You',
          sender_role: user?.role || 'customer',
          sent_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempMessage]);

        const messageToSend = message || '[File attachment]';
        setMessage('');
        const filesToUpload = [...selectedFiles];
        setSelectedFiles([]);

        const sentMessage = await chatService.sendMessage(currentSession.id, messageToSend);

        if (filesToUpload.length > 0) {
          const uploadedFiles = await uploadFiles(sentMessage.id);
          sentMessage.attachments = uploadedFiles;
        }

        setMessages(prev => prev.map(msg => 
          msg.id === tempMessage.id ? sentMessage : msg
        ));
      } catch (error) {
        console.error('Failed to send message:', error);
        setMessages(prev => prev.filter(msg => msg.id !== Date.now()));
        setMessage(message);
      } finally {
        setLoading(false);
      }
    } else if (!currentSession) {
      console.warn('No active session yet');
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileDownload = async (fileUrl: string, filename: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${fileUrl}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-gray-900">
              FreeScout Chat Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Welcome, {user?.name || user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white rounded-lg shadow">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-medium text-gray-900">
                Chat Support
              </h2>
            </div>
            
            <div className="h-96 p-6 overflow-y-auto bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 mt-20">
                  <p className="text-lg mb-2">Welcome to FreeScout Chat!</p>
                  <p className="text-sm">Start a conversation by typing a message below.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${
                        msg.sender_role === 'customer' || msg.sender_name === (user?.name || 'You')
                          ? 'justify-end' 
                          : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-lg ${
                          msg.sender_role === 'customer' || msg.sender_name === (user?.name || 'You')
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-200 text-gray-900'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.attachments.map((file) => (
                              <button
                                key={file.id}
                                onClick={() => handleFileDownload(file.url, file.original_filename)}
                                className={`block text-xs p-2 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                                  msg.sender_role === 'customer' ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-900'
                                }`}
                                title="Click to download"
                              >
                                📎 {file.original_filename} ({formatFileSize(file.size)})
                              </button>
                            ))}
                          </div>
                        )}
                        
                        <p className={`text-xs mt-1 ${
                          msg.sender_role === 'customer' || msg.sender_name === (user?.name || 'You')
                            ? 'text-blue-200' 
                            : 'text-gray-500'
                        }`}>
                          {msg.sender_role === 'agent' || msg.sender_role === 'admin' ? 'Support Agent' : msg.sender_name} • {formatTime(msg.sent_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
            
            <div className="border-t px-6 py-4">
              {selectedFiles.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center bg-gray-100 px-3 py-1 rounded-full text-sm">
                      <span className="mr-2">📎 {file.name}</span>
                      <button
                        onClick={() => removeSelectedFile(index)}
                        className="text-red-600 hover:text-red-800 font-bold"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex space-x-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || uploadingFiles}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                  title="Attach files"
                >
                  📎
                </button>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  disabled={loading || uploadingFiles}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  placeholder="Type your message..."
                />
                <button
                  onClick={handleSendMessage}
                  disabled={loading || uploadingFiles || (!message.trim() && selectedFiles.length === 0)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading || uploadingFiles ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Dashboard;
